import * as THREE from 'three';
import type { FlowSample, River } from '../game/River';

const FOAM_COUNT = 420;
const LEAF_COUNT = 36;
const COUNT = FOAM_COUNT + LEAF_COUNT;
/** Flecks live in a window around the duck: this far behind to this far ahead. */
const BEHIND = 10;
const AHEAD = 48;

function makeLeafGeometry(): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0, -0.2);
  shape.quadraticCurveTo(0.11, -0.02, 0, 0.2);
  shape.quadraticCurveTo(-0.11, -0.02, 0, -0.2);
  const geometry = new THREE.ShapeGeometry(shape, 6);
  geometry.rotateX(-Math.PI / 2);
  return geometry;
}

/**
 * Foam bubbles and leaves carried by the real flow field. They are the most
 * honest read of the current: they drift quickly in the fast water and
 * whirlpools visibly swallow them.
 */
export class Flecks {
  readonly group = new THREE.Group();
  private readonly foam: THREE.InstancedMesh;
  private readonly leaves: THREE.InstancedMesh;
  private readonly x = new Float32Array(COUNT);
  private readonly z = new Float32Array(COUNT);
  private readonly spin = new Float32Array(COUNT);
  private readonly sample: FlowSample = { x: 0, z: 0, speed: 0, whirl: 0 };
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly scale = new THREE.Vector3();
  private readonly pos = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);

  constructor(
    private readonly river: River,
    private readonly rng: () => number,
  ) {
    const bubble = new THREE.CircleGeometry(0.055, 8);
    bubble.rotateX(-Math.PI / 2);
    this.foam = new THREE.InstancedMesh(
      bubble,
      new THREE.MeshBasicMaterial({ color: '#f4fbff', transparent: true, opacity: 0.6, depthWrite: false }),
      FOAM_COUNT,
    );
    this.leaves = new THREE.InstancedMesh(
      makeLeafGeometry(),
      new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.9, side: THREE.DoubleSide }),
      LEAF_COUNT,
    );
    const color = new THREE.Color();
    const greens = ['#4f8f32', '#6aa83c', '#3f7a2c', '#86b84a'];
    for (let i = 0; i < LEAF_COUNT; i += 1) {
      this.leaves.setColorAt(i, color.set(greens[Math.floor(this.rng() * greens.length)]));
    }
    for (const mesh of [this.foam, this.leaves]) {
      mesh.frustumCulled = false;
      mesh.renderOrder = 2;
      this.group.add(mesh);
    }
    for (let i = 0; i < COUNT; i += 1) this.spin[i] = this.rng() * Math.PI * 2;
  }

  reset(duckD: number): void {
    for (let i = 0; i < COUNT; i += 1) {
      this.place(i, duckD - BEHIND + this.rng() * (AHEAD + BEHIND));
    }
  }

  update(delta: number, duckD: number): void {
    const max = this.river.maxSpeed();
    for (let i = 0; i < COUNT; i += 1) {
      this.river.flowAt(this.x[i], this.z[i], this.sample);
      this.x[i] += this.sample.x * delta;
      this.z[i] += this.sample.z * delta;

      const d = -this.z[i];
      const hw = this.river.halfWidth(d);
      const offset = Math.abs(this.x[i] - this.river.centerX(d));
      if (d < duckD - BEHIND) this.place(i, duckD + AHEAD - this.rng() * 6);
      else if (d > duckD + AHEAD) this.place(i, duckD - BEHIND + this.rng() * 4);
      else if (offset > hw * 0.97 || (this.sample.whirl > 0.85 && this.rng() < delta * 3)) {
        this.place(i, duckD - BEHIND + this.rng() * (AHEAD + BEHIND));
      }

      const speed01 = this.sample.speed / max;
      this.pos.set(this.x[i], this.river.heightAt(d, this.x[i]) + 0.035, this.z[i]);
      if (i < FOAM_COUNT) {
        // Bubbles stretch only a little in fast water.
        const angle = Math.atan2(-this.sample.x, -this.sample.z);
        this.quat.setFromAxisAngle(this.up, angle);
        this.scale.set(1, 1, 1 + speed01 * 0.8);
        this.matrix.compose(this.pos, this.quat, this.scale);
        this.foam.setMatrixAt(i, this.matrix);
      } else {
        // Leaves turn lazily as they drift; whirlpools spin them fast.
        this.spin[i] += delta * (0.4 + this.sample.whirl * 6);
        this.quat.setFromAxisAngle(this.up, this.spin[i]);
        this.scale.setScalar(1);
        this.pos.y += 0.01;
        this.matrix.compose(this.pos, this.quat, this.scale);
        this.leaves.setMatrixAt(i - FOAM_COUNT, this.matrix);
      }
    }
    this.foam.instanceMatrix.needsUpdate = true;
    this.leaves.instanceMatrix.needsUpdate = true;
  }

  private place(i: number, d: number): void {
    const s = (this.rng() * 2 - 1) * 0.92;
    this.x[i] = this.river.xAt(d, s);
    this.z[i] = -d;
  }

  dispose(): void {
    for (const mesh of [this.foam, this.leaves]) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
  }
}

const WAKE_COUNT = 40;
const RIPPLE_LIFE = 1.1;
const RIPPLE_WHITE = new THREE.Color('#ffffff');
const RIPPLE_WATER = new THREE.Color('#3aa7b8');

/**
 * Ripple rings where the duck pushes through the water. A duck that just
 * drifts moves with the water and leaves almost none; steering, paddling,
 * bumps and landings make them.
 */
export class Wake {
  readonly mesh: THREE.InstancedMesh;
  private readonly x = new Float32Array(WAKE_COUNT);
  private readonly z = new Float32Array(WAKE_COUNT);
  private readonly vx = new Float32Array(WAKE_COUNT);
  private readonly vz = new Float32Array(WAKE_COUNT);
  private readonly y = new Float32Array(WAKE_COUNT);
  private readonly size = new Float32Array(WAKE_COUNT);
  private spawnY = 0;
  /** Current water velocity at the duck: ripples ride the current. */
  private flowX = 0;
  private flowZ = 0;
  private readonly age = new Float32Array(WAKE_COUNT).fill(99);
  private next = 0;
  private timer = 0;
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly scale = new THREE.Vector3();
  private readonly pos = new THREE.Vector3();
  private readonly color = new THREE.Color();

  constructor() {
    const geometry = new THREE.RingGeometry(0.86, 1, 32);
    geometry.rotateX(-Math.PI / 2);
    this.mesh = new THREE.InstancedMesh(
      geometry,
      new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.75, depthWrite: false }),
      WAKE_COUNT,
    );
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    for (let i = 0; i < WAKE_COUNT; i += 1) this.mesh.setColorAt(i, RIPPLE_WATER);
  }

  reset(): void {
    this.age.fill(99);
  }

  /** Water height at the duck, so ripples sit on the surface after drops. */
  setWaterY(y: number): void {
    this.spawnY = y;
  }

  /** A burst of rings, e.g. a paddle stroke, rock hit or landing. */
  splash(x: number, z: number, amount: number): void {
    const n = Math.min(4, 1 + Math.round(amount * 0.6));
    for (let k = 0; k < n; k += 1) this.spawn(x, z, this.flowX, this.flowZ, 0.35 + k * 0.3 + amount * 0.05);
  }

  /**
   * `vx, vz` is the duck's velocity relative to the water, `flowX, flowZ` the
   * water's own velocity; `intensity` 0..1 scales how often ripples appear.
   */
  update(
    delta: number,
    x: number,
    z: number,
    vx: number,
    vz: number,
    flowX: number,
    flowZ: number,
    intensity: number,
  ): void {
    this.flowX = flowX;
    this.flowZ = flowZ;
    this.timer += delta * intensity * 7;
    while (this.timer >= 1) {
      this.timer -= 1;
      // Ripples spread from the duck and drift a little the way it was pushing.
      this.spawn(x, z, flowX + vx * 0.15, flowZ + vz * 0.15, 0.4);
    }
    for (let i = 0; i < WAKE_COUNT; i += 1) {
      this.age[i] += delta;
      const life = this.age[i] / RIPPLE_LIFE;
      if (life >= 1) {
        this.scale.setScalar(0);
      } else {
        this.x[i] += this.vx[i] * delta;
        this.z[i] += this.vz[i] * delta;
        this.scale.setScalar(this.size[i] + life * 1.3);
        // Fade by blending into the water colour instead of per-instance opacity.
        this.mesh.setColorAt(i, this.color.copy(RIPPLE_WHITE).lerp(RIPPLE_WATER, life));
      }
      this.pos.set(this.x[i], this.y[i] + 0.04, this.z[i]);
      this.matrix.compose(this.pos, this.quat, this.scale);
      this.mesh.setMatrixAt(i, this.matrix);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  private spawn(x: number, z: number, vx: number, vz: number, size: number): void {
    const i = this.next;
    this.next = (this.next + 1) % WAKE_COUNT;
    this.x[i] = x;
    this.z[i] = z;
    this.vx[i] = vx;
    this.vz[i] = vz;
    this.y[i] = this.spawnY;
    this.size[i] = size;
    this.age[i] = 0;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
